/**
 * Banners Admin Store
 * State management for sponsor banner administration
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { supabase } from '@/lib/supabase'
import { config } from '@/config'
import { useAuthStore } from '@/stores/auth.store'

export interface SponsorBanner {
    id: string
    title: string
    url: string
    imageUrl?: string
    content?: string
    contentFormat?: 'html' | 'markdown'
    backgroundColor?: string
    useImageAsBackground?: boolean
    imageFit?: 'contain' | 'cover' | 'fill' | 'none'
    active: boolean
    startDate?: string
    endDate?: string
    createdAt: string
}

export const useBannersAdminStore = defineStore('bannersAdmin', () => {
    // State
    const banners = ref<SponsorBanner[]>([])
    const isLoading = ref(false)
    const error = ref<string | null>(null)
    const bannerRotationInterval = ref(6) // Default to 6 seconds
    const bannerRotationLimit = ref(5) // Default to 5 banners

    // Getters
    const activeBanners = computed(() =>
        banners.value.filter(b => b.active)
    )

    // Actions

    /**
     * Load sponsor banners
     */
    async function loadBanners(): Promise<void> {
        isLoading.value = true
        error.value = null
        try {
            const { data, error: dbError } = await supabase
                .from('sponsor_banners')
                .select('*')
                .order('created_at', { ascending: false })

            if (dbError) throw dbError

            banners.value = (data || []).map(b => ({
                id: b.id,
                title: b.title,
                url: b.url,
                imageUrl: b.image_url,
                content: b.content,
                contentFormat: b.content_format || 'html',
                backgroundColor: b.background_color,
                useImageAsBackground: b.use_image_as_background,
                imageFit: b.image_fit || 'contain',
                active: b.active,
                startDate: b.start_date,
                endDate: b.end_date,
                createdAt: b.created_at,
            }))
        } catch (e: any) {
            console.error('Failed to load banners:', e)
            error.value = e.message || 'Failed to load banners'
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Load banner rotation settings
     */
    async function loadBannerSettings(): Promise<void> {
        try {
            const { data, error: dbError } = await supabase
                .from('global_settings')
                .select('*')
                .in('key', ['banner_rotation_interval', 'banner_rotation_limit'])
                .eq('server_name', config.serverName)

            if (dbError) throw dbError

            if (data) {
                const intervalSetting = data.find(d => d.key === 'banner_rotation_interval')
                if (intervalSetting) {
                    bannerRotationInterval.value = parseInt(intervalSetting.value, 10) || 6
                }
                const limitSetting = data.find(d => d.key === 'banner_rotation_limit')
                if (limitSetting) {
                    bannerRotationLimit.value = parseInt(limitSetting.value, 10) || 5
                }
            }
        } catch (e) {
            console.error('Failed to load banner settings:', e)
        }
    }

    /**
     * Update banner rotation interval
     */
    async function updateRotationInterval(seconds: number): Promise<boolean> {
        bannerRotationInterval.value = seconds
        const authStore = useAuthStore()
        const currentUserId = authStore.user?.id || null
        try {
            const { error: dbError } = await supabase
                .from('global_settings')
                .upsert({
                    key: 'banner_rotation_interval',
                    value: String(seconds),
                    server_name: config.serverName,
                    updated_by: currentUserId
                })

            if (dbError) throw dbError
            return true
        } catch (e) {
            console.error('Failed to persist banner interval:', e)
            return false
        }
    }

    /**
     * Update banner rotation limit
     */
    async function updateRotationLimit(limit: number): Promise<boolean> {
        bannerRotationLimit.value = limit
        const authStore = useAuthStore()
        const currentUserId = authStore.user?.id || null
        try {
            const { error: dbError } = await supabase
                .from('global_settings')
                .upsert({
                    key: 'banner_rotation_limit',
                    value: String(limit),
                    server_name: config.serverName,
                    updated_by: currentUserId
                })

            if (dbError) throw dbError
            return true
        } catch (e) {
            console.error('Failed to persist banner limit:', e)
            return false
        }
    }

    /**
     * Create a new sponsor banner
     */
    async function createBanner(banner: Omit<SponsorBanner, 'id' | 'createdAt'>): Promise<boolean> {
        isLoading.value = true
        error.value = null
        try {
            const { data, error: dbError } = await supabase
                .from('sponsor_banners')
                .insert({
                    title: banner.title,
                    url: banner.url,
                    image_url: banner.imageUrl,
                    content: banner.content,
                    content_format: banner.contentFormat || 'html',
                    background_color: banner.backgroundColor,
                    use_image_as_background: banner.useImageAsBackground,
                    image_fit: banner.imageFit || 'contain',
                    active: banner.active,
                    start_date: banner.startDate,
                    end_date: banner.endDate,
                })
                .select()
                .single()

            if (dbError) throw dbError

            banners.value.unshift({
                id: data.id,
                title: data.title,
                url: data.url,
                imageUrl: data.image_url,
                content: data.content,
                contentFormat: data.content_format || 'html',
                backgroundColor: data.background_color,
                useImageAsBackground: data.use_image_as_background,
                imageFit: data.image_fit || 'contain',
                active: data.active,
                startDate: data.start_date,
                endDate: data.end_date,
                createdAt: data.created_at,
            })

            return true
        } catch (e: any) {
            console.error('Failed to create banner:', e)
            error.value = e.message || 'Unknown error creating banner'
            return false
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Update an existing sponsor banner
     */
    async function updateBanner(id: string, updates: Partial<Omit<SponsorBanner, 'id' | 'createdAt'>>): Promise<boolean> {
        isLoading.value = true
        error.value = null
        try {
            const { error: dbError } = await supabase
                .from('sponsor_banners')
                .update({
                    title: updates.title,
                    url: updates.url,
                    image_url: updates.imageUrl,
                    content: updates.content,
                    content_format: updates.contentFormat,
                    background_color: updates.backgroundColor,
                    use_image_as_background: updates.useImageAsBackground,
                    image_fit: updates.imageFit,
                    active: updates.active,
                    start_date: updates.startDate,
                    end_date: updates.endDate
                })
                .eq('id', id)

            if (dbError) throw dbError

            // Update local state
            const index = banners.value.findIndex(b => b.id === id)
            if (index !== -1) {
                banners.value[index] = { ...banners.value[index], ...updates }
            }

            return true
        } catch (e: any) {
            console.error('Failed to update banner:', e)
            error.value = e.message || 'Unknown error updating banner'
            return false
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Toggle banner active state
     */
    async function toggleBanner(bannerId: string): Promise<boolean> {
        const banner = banners.value.find(b => b.id === bannerId)
        if (!banner) return false

        try {
            const { error: dbError } = await supabase
                .from('sponsor_banners')
                .update({ active: !banner.active })
                .eq('id', bannerId)

            if (dbError) throw dbError

            banner.active = !banner.active
            return true
        } catch (e) {
            console.error('Failed to toggle banner:', e)
            return false
        }
    }

    /**
     * Delete a banner
     */
    async function deleteBanner(bannerId: string): Promise<boolean> {
        isLoading.value = true
        error.value = null
        try {
            const { error: dbError } = await supabase
                .from('sponsor_banners')
                .delete()
                .eq('id', bannerId)

            if (dbError) throw dbError

            banners.value = banners.value.filter(b => b.id !== bannerId)
            return true
        } catch (e: any) {
            console.error('Failed to delete banner:', e)
            error.value = e.message || 'Failed to delete banner'
            return false
        } finally {
            isLoading.value = false
        }
    }

    return {
        banners,
        activeBanners,
        isLoading,
        error,
        bannerRotationInterval,
        bannerRotationLimit,
        loadBanners,
        loadBannerSettings,
        updateRotationInterval,
        updateRotationLimit,
        createBanner,
        updateBanner,
        toggleBanner,
        deleteBanner
    }
})
